import React, { useState, useEffect, useCallback } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import api, { getImageUrl } from "../api/client";
import { AlertOctagon, Search, FileDown, CheckCircle2, XCircle, Clock, Filter, Eye, Pencil, Trash2, X } from "lucide-react";

const DEPARTMENTS = ["Mechanical", "Electronics", "Software", "IT", "Client", "Other"];

export function ManagerIncidentScreen() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);
  
  // Search & Filters State
  const [search, setSearch] = useState("");
  const [selectedDept, setSelectedDept] = useState("all");
  const [selectedSolved, setSelectedSolved] = useState("all");
  const [selectedApproval, setSelectedApproval] = useState("all");

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const loadIncidents = useCallback(async () => {
    try {
      const { data } = await api.get("/incidents");
      setIncidents(data.data || []);
    } catch (err) {
      console.error("Error loading incidents:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIncidents();
  }, [loadIncidents]);

  const handleStatusChange = async (id, status) => {
    setActioningId(id);
    try {
      await api.put(`/incidents/${id}/status`, { status });
      await loadIncidents();
    } catch (err) {
      console.error("Error updating incident status:", err);
      alert("Failed to update status");
    } finally {
      setActioningId(null);
    }
  };

  const handleExportExcel = () => {
    const token = localStorage.getItem("fp_token") || "";
    window.open(`https://api.goldpe.tech/api/export/incidents?token=${token}`, "_blank");
  };

  const openEditModal = (inc) => {
    const deptIsCustom = !DEPARTMENTS.includes(inc.department);
    setEditForm({
      id: inc.id,
      problem_statement: inc.problem_statement || "",
      department: deptIsCustom ? "Other" : inc.department,
      custom_department: deptIsCustom ? inc.department : "",
      incident_position: inc.incident_position || "",
      has_solution: inc.has_solution === 1 || inc.has_solution === true,
      solution: inc.solution || "",
      total_time_taken: inc.total_time_taken || "",
      no_solution_reason: inc.no_solution_reason || "",
      solved_by: inc.solved_by || "",
      incident_date: inc.incident_date ? String(inc.incident_date).slice(0, 16) : "",
      solution_date: inc.solution_date ? String(inc.solution_date).slice(0, 16) : "",
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm) return;
    setEditSaving(true);
    try {
      const finalDept = editForm.department === "Other" ? editForm.custom_department || "Other" : editForm.department;
      await api.put(`/incidents/${editForm.id}`, {
        problem_statement: editForm.problem_statement,
        department: finalDept,
        incident_position: editForm.incident_position || null,
        has_solution: editForm.has_solution,
        solution: editForm.has_solution ? editForm.solution : null,
        total_time_taken: editForm.has_solution ? editForm.total_time_taken : null,
        no_solution_reason: !editForm.has_solution ? editForm.no_solution_reason : null,
        solved_by: editForm.has_solution ? editForm.solved_by : null,
        incident_date: editForm.incident_date || null,
        solution_date: editForm.has_solution ? editForm.solution_date : null,
      });
      setEditModalOpen(false);
      setEditForm(null);
      await loadIncidents();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update incident");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (incId) => {
    if (!confirm("Are you sure you want to delete this incident? This action cannot be undone.")) return;
    try {
      await api.delete(`/incidents/${incId}`);
      await loadIncidents();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete incident");
    }
  };

  const formatDisplayDateTime = (dtStr) => {
    if (!dtStr) return "N/A";
    try {
      const d = new Date(dtStr);
      if (isNaN(d.getTime())) return dtStr;
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return dtStr;
    }
  };

  // Compute Stats
  const totalIncidents = incidents.length;
  const totalPending = incidents.filter(i => (i.manager_status || "pending") === "pending").length;
  const totalApproved = incidents.filter(i => i.manager_status === "approved").length;
  const totalRejected = incidents.filter(i => i.manager_status === "rejected").length;

  // Filtered List
  const filteredIncidents = incidents.filter(inc => {
    // 1. Search text match
    const problemMatch = (inc.problem_statement || "").toLowerCase().includes(search.toLowerCase());
    const nameMatch = (inc.empName || "").toLowerCase().includes(search.toLowerCase());
    const deptMatch = (inc.department || "").toLowerCase().includes(search.toLowerCase());
    const matchesSearch = problemMatch || nameMatch || deptMatch;

    // 2. Department filter
    const matchesDept = selectedDept === "all" || String(inc.department).toLowerCase() === selectedDept.toLowerCase();

    // 3. Solved filter
    const isSolved = inc.has_solution === 1 || inc.has_solution === true;
    const matchesSolved = selectedSolved === "all" ||
      (selectedSolved === "solved" && isSolved) ||
      (selectedSolved === "unsolved" && !isSolved);

    // 4. Approval status filter
    const currentApproval = inc.manager_status || "pending";
    const matchesApproval = selectedApproval === "all" || currentApproval === selectedApproval;

    return matchesSearch && matchesDept && matchesSolved && matchesApproval;
  });

  if (loading) {
    return <div style={{ padding: 32, textAlign: "center", color: TOKENS.muted, fontSize: 14 }}>Loading incident register...</div>;
  }

  return (
    <>
    <div>
      {/* Header and Excel Export */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <SectionLabel icon={<AlertOctagon size={18} />} text="Employee Incident Register" />
        <button
          onClick={handleExportExcel}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: TOKENS.navyDeep,
            color: TOKENS.gold,
            border: `1.5px solid ${TOKENS.gold}80`,
            borderRadius: 10,
            padding: "8px 16px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            transition: "all 0.2s",
            boxShadow: `0 4px 10px ${TOKENS.navyDeep}20`,
          }}
          className="btn-gold-glow"
        >
          <FileDown size={16} /> Export to Excel
        </button>
      </div>

      {/* Stats Cards */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <Card style={{ flex: "1 1 200px", padding: 16, borderLeft: `4px solid ${TOKENS.warning}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.muted }}>Pending Review</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: TOKENS.warning, marginTop: 4 }}>{totalPending}</div>
        </Card>
        <Card style={{ flex: "1 1 200px", padding: 16, borderLeft: `4px solid ${TOKENS.success}`, background: TOKENS.successBg }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.success }}>Approved / Completed</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: TOKENS.success, marginTop: 4 }}>{totalApproved}</div>
        </Card>
        <Card style={{ flex: "1 1 200px", padding: 16, borderLeft: `4px solid ${TOKENS.danger}`, background: TOKENS.dangerBg }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.danger }}>Disapproved</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: TOKENS.danger, marginTop: 4 }}>{totalRejected}</div>
        </Card>
        <Card style={{ flex: "1 1 200px", padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.muted }}>Total Logged</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: TOKENS.navyDeep, marginTop: 4 }}>{totalIncidents}</div>
        </Card>
      </div>

      {/* Search & Filter Controls */}
      <Card style={{ padding: 16, marginBottom: 20, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: TOKENS.navyDeep, fontWeight: 700, fontSize: 13, borderBottom: `1px solid ${TOKENS.border}`, paddingBottom: 8 }}>
          <Filter size={15} /> Search & Filters
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          {/* Search Input */}
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Search problem, name, department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px 8px 34px",
                borderRadius: 8,
                border: `1px solid ${TOKENS.border}`,
                background: TOKENS.cream,
                fontSize: 13,
                outline: "none",
                color: TOKENS.ink
              }}
            />
            <Search size={15} color={TOKENS.muted} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
          </div>

          {/* Department Filter */}
          <div>
            <select
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${TOKENS.border}`,
                background: TOKENS.cream,
                fontSize: 13,
                outline: "none",
                color: TOKENS.ink
              }}
            >
              <option value="all">All Departments</option>
              <option value="mechanical">Mechanical</option>
              <option value="electronics">Electronics</option>
              <option value="software">Software</option>
              <option value="it">IT</option>
              <option value="client">Client</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Solved Status Filter */}
          <div>
            <select
              value={selectedSolved}
              onChange={(e) => setSelectedSolved(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${TOKENS.border}`,
                background: TOKENS.cream,
                fontSize: 13,
                outline: "none",
                color: TOKENS.ink
              }}
            >
              <option value="all">All Solution States</option>
              <option value="solved">Solved / Has Solution</option>
              <option value="unsolved">Unsolved / Obstacle</option>
            </select>
          </div>

          {/* Manager Approval Filter */}
          <div>
            <select
              value={selectedApproval}
              onChange={(e) => setSelectedApproval(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${TOKENS.border}`,
                background: TOKENS.cream,
                fontSize: 13,
                outline: "none",
                color: TOKENS.ink
              }}
            >
              <option value="all">All Approval States</option>
              <option value="pending">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Incident List */}
      {filteredIncidents.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 40, color: TOKENS.muted }}>
          <AlertOctagon size={36} color={TOKENS.muted} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
          <div style={{ fontSize: 14, fontWeight: 700 }}>No matching incidents found.</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Refine your filters or search term.</div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {filteredIncidents.map((inc) => {
            const isSolved = inc.has_solution === 1 || inc.has_solution === true;
            const approval = inc.manager_status || "pending";
            const isLoading = actioningId === inc.id;

            return (
              <Card
                key={inc.id}
                style={{
                  padding: 18,
                  borderLeft: `4px solid ${isSolved ? TOKENS.success : TOKENS.danger}`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                  {/* Left content */}
                  <div style={{ flex: 1, minWidth: 260 }}>
                    {/* Employee Profile info */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          background: TOKENS.goldPale,
                          border: `1.5px solid ${TOKENS.gold}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: TOKENS.gold,
                          fontSize: 13,
                          fontWeight: 800,
                        }}
                      >
                        {inc.empInitials || inc.empName?.slice(0, 2).toUpperCase() || "??"}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: TOKENS.navyDeep }}>
                          {inc.empName || "Unknown Employee"}
                        </div>
                        <div style={{ fontSize: 11, color: TOKENS.muted }}>
                          Logged At: {formatDisplayDateTime(inc.created_at)}
                        </div>
                      </div>
                    </div>

                    {/* Department, Date */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                      <span style={{ fontSize: 11.5, color: TOKENS.navySoft, fontWeight: 800, background: "rgba(29,67,115,0.08)", padding: "2px 8px", borderRadius: 6 }}>
                        {inc.department} Dept
                      </span>
                      {inc.incident_position && (
                        <span style={{ fontSize: 11.5, color: TOKENS.navySoft, fontWeight: 700, background: "rgba(29,67,115,0.06)", padding: "2px 8px", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 3 }}>
                          📍 {inc.incident_position}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: TOKENS.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Clock size={12} /> Incident Time: {formatDisplayDateTime(inc.incident_date)}
                      </span>
                    </div>

                    {/* Problem Statement */}
                    <div style={{ fontSize: 13.5, color: TOKENS.ink, lineHeight: 1.5, fontWeight: 500, whiteSpace: "pre-wrap", background: "#FAFBFD", padding: 12, borderRadius: 10, border: `1.5px solid ${TOKENS.border}` }}>
                      {inc.problem_statement}
                    </div>

                    {/* Solved details */}
                    {isSolved ? (
                      <div style={{ background: TOKENS.successBg, padding: 12, borderRadius: 10, border: `1.5px solid ${TOKENS.success}20`, marginTop: 10 }}>
                        <div style={{ fontWeight: 700, color: TOKENS.success, marginBottom: 4, fontSize: 11.5 }}>
                          ✓ Solution Provided:
                        </div>
                        <div style={{ color: TOKENS.ink, fontSize: 12.5, lineHeight: 1.4 }}>{inc.solution}</div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, borderTop: `1px solid ${TOKENS.success}25`, paddingTop: 8, marginTop: 8, fontSize: 11 }}>
                          <div>
                            <span style={{ color: TOKENS.muted, fontWeight: 600 }}>Solved By:</span>
                            <div style={{ color: TOKENS.ink, fontWeight: 700 }}>{inc.solved_by || "N/A"}</div>
                          </div>
                          <div>
                            <span style={{ color: TOKENS.muted, fontWeight: 600 }}>Time Taken:</span>
                            <div style={{ color: TOKENS.ink, fontWeight: 700 }}>{inc.total_time_taken || "N/A"}</div>
                          </div>
                        </div>

                        {inc.solution_date && (
                          <div style={{ fontSize: 10.5, color: TOKENS.muted, marginTop: 6 }}>
                            Solved at: <strong>{formatDisplayDateTime(inc.solution_date)}</strong>
                          </div>
                        )}
                      </div>
                    ) : (
                      inc.no_solution_reason && (
                        <div style={{ background: TOKENS.dangerBg, padding: 12, borderRadius: 10, border: `1.5px solid ${TOKENS.danger}20`, marginTop: 10 }}>
                          <div style={{ fontWeight: 700, color: TOKENS.danger, marginBottom: 4, fontSize: 11.5 }}>
                            ⚠️ Roadblock / Obstacle Details:
                          </div>
                          <div style={{ color: TOKENS.ink, fontSize: 12.5, lineHeight: 1.4 }}>{inc.no_solution_reason}</div>
                        </div>
                      )
                    )}

                    {/* Photo upload attachment */}
                    {inc.photoUrl && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11.5, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 6 }}>📷 Attachment:</div>
                        <a href={getImageUrl(inc.photoUrl)} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", color: TOKENS.navyDeep }}>
                          <img
                            src={getImageUrl(inc.photoUrl)}
                            alt="Incident Attachment"
                            style={{
                              maxWidth: 160,
                              maxHeight: 100,
                              borderRadius: 8,
                              border: `1.5px solid ${TOKENS.border}`,
                              objectFit: "cover"
                            }}
                          />
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ fontSize: 11, color: TOKENS.navySoft, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                              <Eye size={12} /> View Full Attachment
                            </span>
                          </div>
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Right side status / approval controls */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10, flexShrink: 0 }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: TOKENS.muted }}>Employee:</span>
                      <StatusPill status={isSolved ? "solved" : "unsolved"} />
                    </div>
                    
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
                      <span style={{ fontSize: 11, color: TOKENS.muted }}>Manager:</span>
                      <StatusPill status={approval} />
                    </div>

                    {approval === "pending" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14, width: "100%", minWidth: 150 }}>
                        <button
                          onClick={() => handleStatusChange(inc.id, "approved")}
                          disabled={isLoading}
                          style={{
                            background: TOKENS.success,
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "8px 14px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: isLoading ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            opacity: isLoading ? 0.6 : 1,
                            transition: "all 0.15s"
                          }}
                        >
                          <CheckCircle2 size={14} /> Approve Completed
                        </button>
                        <button
                          onClick={() => handleStatusChange(inc.id, "rejected")}
                          disabled={isLoading}
                          style={{
                            background: TOKENS.danger,
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "8px 14px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: isLoading ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            opacity: isLoading ? 0.6 : 1,
                            transition: "all 0.15s"
                          }}
                        >
                          <XCircle size={14} /> Disapprove
                        </button>
                      </div>
                    )}

                    {/* Edit & Delete Buttons */}
                    <div style={{ display: "flex", gap: 8, marginTop: 14, width: "100%", minWidth: 150 }}>
                      <button
                        onClick={() => openEditModal(inc)}
                        style={{
                          flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                          background: TOKENS.cream, border: `1.5px solid ${TOKENS.border}`,
                          borderRadius: 8, padding: "8px 12px", fontSize: 11.5, fontWeight: 700,
                          color: TOKENS.navyDeep, cursor: "pointer", transition: "all 0.15s",
                        }}
                      >
                        <Pencil size={13} /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(inc.id)}
                        style={{
                          flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                          background: TOKENS.dangerBg, border: `1.5px solid ${TOKENS.danger}30`,
                          borderRadius: 8, padding: "8px 12px", fontSize: 11.5, fontWeight: 700,
                          color: TOKENS.danger, cursor: "pointer", transition: "all 0.15s",
                        }}
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>

      {/* Edit Incident Modal */}
      {editModalOpen && editForm && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(10,20,40,0.65)", display: "flex", alignItems: "flex-start", paddingTop: 80, justifyContent: "center",
          zIndex: 999, padding: 16
        }}>
          <div style={{
            background: "#fff", borderRadius: 20, padding: 24, maxWidth: 520, width: "100%",
            maxHeight: "85vh", overflowY: "auto",
            boxShadow: "0 20px 40px rgba(0,0,0,0.25)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderBottom: `1px solid ${TOKENS.border}`, paddingBottom: 10 }}>
              <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                ✏️ Edit Incident
              </h3>
              <button onClick={() => { setEditModalOpen(false); setEditForm(null); }} style={{ background: "none", border: "none", cursor: "pointer" }}>
                <X size={18} color={TOKENS.muted} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>PROBLEM STATEMENT *</label>
              <textarea
                value={editForm.problem_statement}
                onChange={(e) => setEditForm(f => ({ ...f, problem_statement: e.target.value }))}
                rows={3} required
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: TOKENS.cream, resize: "none", marginBottom: 14 }}
              />

              <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>DEPARTMENT *</label>
              <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                {DEPARTMENTS.map((dept) => (
                  <button type="button" key={dept}
                    onClick={() => setEditForm(f => ({ ...f, department: dept }))}
                    style={{
                      padding: "6px 12px", borderRadius: 10,
                      border: `1.5px solid ${editForm.department === dept ? TOKENS.navyDeep : TOKENS.border}`,
                      background: editForm.department === dept ? TOKENS.navyDeep : "#fff",
                      color: editForm.department === dept ? "#fff" : TOKENS.ink,
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                  >{dept}</button>
                ))}
              </div>
              {editForm.department === "Other" && (
                <input type="text" placeholder="Type department name…" value={editForm.custom_department}
                  onChange={(e) => setEditForm(f => ({ ...f, custom_department: e.target.value }))} required
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: TOKENS.cream, marginBottom: 14 }}
                />
              )}

              <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>INCIDENT POSITION</label>
              <input type="text" placeholder="e.g. Machine Area, Assembly Line 2…" value={editForm.incident_position}
                onChange={(e) => setEditForm(f => ({ ...f, incident_position: e.target.value }))}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: TOKENS.cream, marginBottom: 14 }}
              />

              <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>DATE & TIME OF INCIDENT</label>
              <input type="datetime-local" value={editForm.incident_date}
                onChange={(e) => setEditForm(f => ({ ...f, incident_date: e.target.value }))}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: TOKENS.cream, marginBottom: 14 }}
              />

              <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>SOLUTION FOUND?</label>
              <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                <button type="button" onClick={() => setEditForm(f => ({ ...f, has_solution: true }))} style={{
                  flex: 1, padding: "10px", borderRadius: 10,
                  border: `1.5px solid ${editForm.has_solution ? TOKENS.success : TOKENS.border}`,
                  background: editForm.has_solution ? TOKENS.successBg : "#fff",
                  color: editForm.has_solution ? TOKENS.success : TOKENS.ink,
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}>✓ Yes</button>
                <button type="button" onClick={() => setEditForm(f => ({ ...f, has_solution: false }))} style={{
                  flex: 1, padding: "10px", borderRadius: 10,
                  border: `1.5px solid ${!editForm.has_solution ? TOKENS.danger : TOKENS.border}`,
                  background: !editForm.has_solution ? TOKENS.dangerBg : "#fff",
                  color: !editForm.has_solution ? TOKENS.danger : TOKENS.ink,
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}>✗ No</button>
              </div>

              {editForm.has_solution ? (
                <div style={{ background: `${TOKENS.successBg}50`, padding: 12, borderRadius: 12, border: `1px solid ${TOKENS.border}`, marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>SOLUTION DETAILS *</label>
                  <textarea value={editForm.solution} onChange={(e) => setEditForm(f => ({ ...f, solution: e.target.value }))} rows={2} required
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: "#fff", resize: "none", marginBottom: 12 }}
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>TOTAL TIME TAKEN</label>
                      <input type="text" placeholder="e.g. 2 hours" value={editForm.total_time_taken}
                        onChange={(e) => setEditForm(f => ({ ...f, total_time_taken: e.target.value }))}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: "#fff" }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>SOLVED BY</label>
                      <input type="text" placeholder="Name" value={editForm.solved_by}
                        onChange={(e) => setEditForm(f => ({ ...f, solved_by: e.target.value }))}
                        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: "#fff" }}
                      />
                    </div>
                  </div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>SOLUTION DATE & TIME</label>
                  <input type="datetime-local" value={editForm.solution_date}
                    onChange={(e) => setEditForm(f => ({ ...f, solution_date: e.target.value }))}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: "#fff" }}
                  />
                </div>
              ) : (
                <div style={{ background: `${TOKENS.dangerBg}50`, padding: 12, borderRadius: 12, border: `1px solid ${TOKENS.border}`, marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>WHY AND WHAT IS PREVENTING A SOLUTION? *</label>
                  <textarea value={editForm.no_solution_reason} onChange={(e) => setEditForm(f => ({ ...f, no_solution_reason: e.target.value }))} rows={2} required
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: "#fff", resize: "none" }}
                  />
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button type="button" onClick={() => { setEditModalOpen(false); setEditForm(null); }}
                  style={{ flex: 1, padding: "12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, background: "#fff", color: TOKENS.ink, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                >Cancel</button>
                <button type="submit" disabled={editSaving}
                  style={{ flex: 1, padding: "12px", borderRadius: 10, border: "none", background: TOKENS.navyDeep, color: "#fff", fontWeight: 700, fontSize: 13, cursor: editSaving ? "not-allowed" : "pointer", opacity: editSaving ? 0.6 : 1 }}
                >{editSaving ? "Saving…" : "Update Incident"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
